<template>
  <v-container fluid>
    <BaseBreadcrumb
      :title="`Controllers for ${place.Name}`"
      :breadcrumbs="[
        {
          text: place.AddressText,
          disabled: true
        }
      ]"
    />
    <v-row v-if="controllers.length && !loadingControllers">
      <v-col v-for="(controller, i) in controllers" :key="i" cols="12">
        <v-card
          :dark="theme === 'dark'"
          :color="theme === 'dark' ? 'secondary lighten-1' : ''"
        >
          <v-toolbar :height="$vuetify.breakpoint.smAndDown ? '96' : ''">
            <v-row
              class="align-center no-gutters"
              :class="$vuetify.breakpoint.smAndDown ? 'justify-end' : ''"
            >
              <v-btn
                v-if="user.isAdmin || user.isPlaceAdmin"
                color="accent lighten-2"
                icon
                small
                class="mr-3"
                @click="openEditControllerDialog(controller)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-icon small class="mr-1">mdi-cellphone-android</v-icon>
              {{ controller.PhoneNumber }}
              ({{ controller.Gates.length || 0 }})
              {{ controller.Gates.length === 1 ? 'gate' : 'gates' }}
              <v-spacer></v-spacer>
              <v-btn
                color="error"
                :class="$vuetify.breakpoint.smAndDown ? 'mt-2' : ''"
                @click="sendCommandToController(controller, 'RESET')"
              >
                {{ $vuetify.lang.t('$vuetify.GATE_CONTROLLERS.RESTART') }}
              </v-btn>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-if="controller.Version >= 3"
                    icon
                    class="ml-2"
                    :loading="
                      refreshingControllers &&
                        selectedController &&
                        controller.Id === selectedController.Id
                    "
                    v-on="on"
                    @click="sendCommandToController(controller, 'STATUS')"
                  >
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>
                  {{ $vuetify.lang.t('$vuetify.CONTROLLERS.REFRESH') }}
                </span>
              </v-tooltip>
              <v-tooltip v-if="user.isAdmin || user.isPlaceAdmin" top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-if="controller.Version >= 3"
                    icon
                    class="ml-2"
                    v-on="on"
                    @click="openControllerLogsDialog(controller)"
                  >
                    <v-icon>mdi-information</v-icon>
                  </v-btn>
                </template>
                <span>
                  {{ $vuetify.lang.t('$vuetify.CONTROLLERS.CONTROLLER_LOGS') }}
                </span>
              </v-tooltip>
              <v-tooltip v-if="user.isAdmin || user.isPlaceAdmin" top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-if="controller.Version >= 3"
                    icon
                    class="ml-2"
                    v-on="on"
                    @click="openFirmwareUpdateDialog(controller)"
                  >
                    <v-icon>mdi-file</v-icon>
                  </v-btn>
                </template>
                <span>
                  {{ $vuetify.lang.t('$vuetify.CONTROLLERS.UPDATE_FIRMWARE') }}
                </span>
              </v-tooltip>
            </v-row>
          </v-toolbar>
          <v-card-text>
            <v-row
              class="align-center no-gutters"
              :class="$vuetify.breakpoint.smAndDown ? 'justify-end' : ''"
            >
              <v-col cols="12">
                <div
                  :class="
                    $vuetify.breakpoint.smAndDown ? 'row col-12 pl-3' : ''
                  "
                >
                  {{ controller.Description }}
                </div>
              </v-col>
            </v-row>
            <!-- Uptime -->
            <v-col cols="12 ma-0 pa-0">
              <span
                v-if="controller.Version >= 3"
                class="mr-2 font-weight-bold"
                :class="theme === 'dark' ? 'white--text' : ''"
              >
                <v-icon small>mdi-clock</v-icon>
                Uptime:
                <span class="font-weight-regular">
                  {{ secondsToHms(controller.Uptime) }} ({{
                    controller.Uptime
                  }}s)
                </span>
              </span>
            </v-col>
            <!-- EthernetIp -->
            <v-col cols="12 ma-0 pa-0">
              <span
                v-if="controller.Version >= 3"
                class="mr-2 font-weight-bold"
                :class="theme === 'dark' ? 'white--text' : ''"
              >
                <v-icon small>mdi-ethernet-cable</v-icon>
                Ethernet ip:
                <span class="font-weight-regular">
                  {{ controller.EthernetIP }}
                </span>
              </span>
            </v-col>
            <!-- WifiIp -->
            <v-col cols="12 ma-0 pa-0">
              <span
                v-if="controller.Version >= 3"
                class="mr-2 font-weight-bold"
                :class="theme === 'dark' ? 'white--text' : ''"
              >
                <v-icon small>mdi-wifi</v-icon>
                Wifi ip:
                <span class="font-weight-regular">
                  {{ controller.WifiIP }}
                </span>
              </span>
            </v-col>
            <!-- Firmware -->
            <v-col cols="12 ma-0 pa-0">
              <span
                v-if="controller.Version >= 3"
                class="mr-2 font-weight-bold"
                :class="theme === 'dark' ? 'white--text' : ''"
              >
                <v-icon small>mdi-chip</v-icon>
                Firmware version:
                <span class="font-weight-regular">
                  {{ controller.FirmwareVersion }}
                </span>
              </span>
            </v-col>
            <!-- PinStateList -->
            <v-col cols="12 ma-0 pa-0">
              <span
                class="mr-2 font-weight-bold"
                :class="theme === 'dark' ? 'white--text' : ''"
              >
                <v-icon small>mdi-pin-outline</v-icon>
                {{ $vuetify.lang.t('$vuetify.GATE_CONTROLLERS.PIN_STATES') }}:
                <span class="font-weight-regular">
                  {{ controller.PinStateList || '?' }}
                </span>
              </span>
            </v-col>
            <!-- OutputPinStateList -->
            <v-col cols="12 ma-0 pa-0">
              <span
                class="mr-2 font-weight-bold"
                :class="theme === 'dark' ? 'white--text' : ''"
              >
                <v-icon small>mdi-pin-outline</v-icon>
                {{
                  $vuetify.lang.t(
                    '$vuetify.GATE_CONTROLLERS.OUTPUT_PIN_STATES'
                  )
                }}:
                <span class="font-weight-regular">
                  {{ controller.OutputPinStateList || '?' }}
                </span>
              </span>
            </v-col>
            <!-- Last Heartbeat -->
            <v-col cols="12 ma-0 pa-0">
              <span
                class="mr-2 font-weight-bold"
                :class="theme === 'dark' ? 'white--text' : ''"
              >
                <v-icon small>mdi-heart-flash</v-icon>
                {{
                  $vuetify.lang.t('$vuetify.GATE_CONTROLLERS.LAST_HEARTBEAT')
                }}:
                <span class="font-weight-regular">
                  {{ dateEE(controller.LastHeartbeat) || '?' }}
                </span>
              </span>
            </v-col>
            <!-- Signal -->
            <v-col cols="12 ma-0 pa-0">
              <span
                class="mr-2 font-weight-bold"
                :class="theme === 'dark' ? 'white--text' : ''"
              >
                <div
                  v-if="
                    controller.Version >= 3 &&
                      returnValueFromZeroToHundred(controller.LastRSSI) <= 25
                  "
                  class="signal-bars mt1 sizing-box good four-bars"
                >
                  <div class="first-bar bar"></div>
                  <div class="second-bar bar"></div>
                  <div class="third-bar bar"></div>
                  <div class="fourth-bar bar"></div>
                  <div class="fifth-bar bar"></div>
                </div>
                <div
                  v-if="
                    controller.Version >= 3 &&
                      returnValueFromZeroToHundred(controller.LastRSSI) > 25 &&
                      returnValueFromZeroToHundred(controller.LastRSSI) <= 40
                  "
                  class="signal-bars mt1 sizing-box ok three-bars"
                >
                  <div class="first-bar bar"></div>
                  <div class="second-bar bar"></div>
                  <div class="third-bar bar"></div>
                  <div class="fourth-bar bar"></div>
                  <div class="fifth-bar bar"></div>
                </div>
                <div
                  v-if="
                    controller.Version >= 3 &&
                      returnValueFromZeroToHundred(controller.LastRSSI) > 40 &&
                      returnValueFromZeroToHundred(controller.LastRSSI) <= 65
                  "
                  class="signal-bars mt1 sizing-box bad two-bars"
                >
                  <div class="first-bar bar"></div>
                  <div class="second-bar bar"></div>
                  <div class="third-bar bar"></div>
                  <div class="fourth-bar bar"></div>
                  <div class="fifth-bar bar"></div>
                </div>
                <div
                  v-if="
                    controller.Version >= 3 &&
                      returnValueFromZeroToHundred(controller.LastRSSI) >= 98
                  "
                  class="signal-bars mt1 sizing-box bad one-bar"
                >
                  <div class="first-bar bar"></div>
                  <div class="second-bar bar"></div>
                  <div class="third-bar bar"></div>
                  <div class="fourth-bar bar"></div>
                  <div class="fifth-bar bar"></div>
                </div>
                <v-icon v-if="controller.Version < 3" small>mdi-signal</v-icon>
                {{
                  $vuetify.lang.t('$vuetify.GATE_CONTROLLERS.SIGNAL_STRENGTH')
                }}:
                <span class="font-weight-regular">
                  {{ controller.LastRSSI || '?' }}
                </span>
              </span>
            </v-col>
            <v-col
              v-if="controller.Inputs && controller.Inputs.length"
              cols="12"
            >
              <v-row
                v-for="input in controller.Inputs"
                :key="input.ID"
                class="d-flex align-center"
              >
                {{ input.Pin }} - {{ input.Description }}
                <div
                  style="width: 12px; height: 12px; border-radius: 50%; margin-left: 8px;"
                  :style="{
                    'background-color': input.Status ? '#4CAF50' : '#c8697e'
                  }"
                ></div>
              </v-row>
            </v-col>
            <!-- <v-radio-group v-model="sendMode" label="Commands will be sent by">
              <v-radio value="AUTO" label="Auto" color="accent"></v-radio>
              <v-radio value="NETWORK" label="Network" color="accent"></v-radio>
              <v-radio value="SMS" label="SMS" color="accent"></v-radio>
            </v-radio-group> -->
            <v-simple-table
              v-if="controller.Gates.length"
              :dark="theme === 'dark'"
            >
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">
                      {{ $vuetify.lang.t('$vuetify.DESCRIPTION') }}
                    </th>
                    <th class="text-left">
                      {{ $vuetify.lang.t('$vuetify.SERVICE') }}
                    </th>
                    <th class="text-right">
                      {{ $vuetify.lang.t('$vuetify.GATE_CONTROLLERS.ACTIONS') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(gate, i2) in controller.Gates" :key="i2">
                    <td>
                      {{ gate.GateNumber }}
                      {{ gate.Description }}
                    </td>
                    <td>
                      <span
                        left
                        class="white--text font-weight-bold caption secondary lighten-2"
                        style="border-radius: 20px; padding: 4px 6px;"
                      >
                        {{ gate.Activity }}
                      </span>
                    </td>
                    <td
                      v-if="$vuetify.breakpoint.smAndUp"
                      class="d-flex align-center justify-end"
                    >
                      <v-btn
                        color="success"
                        class="mr-2"
                        text
                        @click="
                          sendOpenCommandToController(
                            controller,
                            controller.Gates[i2].ID
                          )
                        "
                      >
                        {{ $vuetify.lang.t('$vuetify.GATE_CONTROLLERS.OPEN') }}
                      </v-btn>
                      <!-- <v-btn
                        color="success"
                        class="mr-2"
                        text
                        @click="sendCommandToController(controller, 'KEEPOPEN')"
                      >
                        Keep open
                      </v-btn>
                      <v-btn
                        color="success"
                        text
                        @click="
                          sendCommandToController(controller, 'RELEASEOPEN')
                        "
                      >
                        Release keep open
                      </v-btn> -->
                      <v-switch
                        v-if="controller.Version >= 3"
                        :input-value="
                          controller.Gates[i2].isAlwaysOpened ||
                            isInhibitInputHigh(controller, controller.Gates[i2])
                        "
                        :loading="
                          openingOrClosingGate &&
                            selectedController &&
                            selectedController.Id === controller.Id &&
                            selectedGate.ID === selectedController.Gates[i2].ID
                        "
                        @change="switchChange(controller, i2)"
                      >
                      </v-switch>
                      <v-chip
                        v-if="controller.Version >= 3"
                        class="ml-2"
                        :color="
                          controller.Gates[i2].isAlwaysOpened ||
                          isInhibitInputHigh(controller, controller.Gates[i2])
                            ? 'success'
                            : 'gray'
                        "
                      >
                        <div class="d-flex align-center">
                          <span>
                            {{
                              controller.Gates[i2].isAlwaysOpened ||
                              isInhibitInputHigh(
                                controller,
                                controller.Gates[i2]
                              )
                                ? $vuetify.lang.t('$vuetify.CONTROLLERS.OPENED')
                                : $vuetify.lang.t('$vuetify.CONTROLLERS.CLOSED')
                            }}
                          </span>
                          <v-icon style="margin-top: -4px;" class="ml-1">{{
                            controller.Gates[i2].isAlwaysOpened ||
                            isInhibitInputHigh(controller, controller.Gates[i2])
                              ? 'mdi-boom-gate-up-outline'
                              : 'mdi-boom-gate'
                          }}</v-icon>
                        </div>
                      </v-chip>
                    </td>
                    <td v-else class="text-right">
                      <v-menu bottom left>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon v-bind="attrs" v-on="on">
                            <v-icon>mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>

                        <v-list>
                          <v-list-item>
                            <v-list-item-title
                              @click="
                                sendCommandToController(controller, 'OPEN')
                              "
                            >
                              Open
                            </v-list-item-title>
                          </v-list-item>
                          <v-list-item>
                            <!-- <v-list-item-title
                              @click="
                                sendCommandToController(
                                  controller,
                                  'OPEN',
                                  'SMS'
                                )
                              "
                            >
                              Open
                            </v-list-item-title> -->
                            <v-switch
                              v-if="controller.Version >= 3"
                              :input-value="
                                controller.Gates[i2].isAlwaysOpened ||
                                  isInhibitInputHigh(
                                    controller,
                                    controller.Gates[i2]
                                  )
                              "
                              :loading="
                                openingOrClosingGate &&
                                  selectedController &&
                                  selectedController.Id === controller.Id &&
                                  selectedGate.ID ===
                                    selectedController.Gates[i2].ID
                              "
                              @change="switchChange(controller, i2)"
                            >
                            </v-switch>
                          </v-list-item>
                          <!-- <v-list-item>
                            <v-list-item-title
                              @click="
                                sendCommandToController(controller, 'KEEPOPEN')
                              "
                            >
                              Keep open
                            </v-list-item-title>
                          </v-list-item>
                          <v-list-item>
                            <v-list-item-title
                              @click="
                                sendCommandToController(
                                  controller,
                                  'RELEASEOPEN'
                                )
                              "
                            >
                              Release keep open
                            </v-list-item-title>
                          </v-list-item> -->
                        </v-list>
                      </v-menu>
                    </td>
                    <!-- <td>{{ sess.TotalCostInCents / 100 }}</td>
                  <td>
                    {{ sess.UsedFleetCreditCents / 100 }}
                  </td>
                  <td>{{ sess.SelfPaidCents / 100 }}</td> -->
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        v-if="!controllers.length && !loading.state"
        cols="12"
        class="d-flex justify-center"
      >
        No controllers
      </v-col>
      <v-col cols="12">
        <v-card-actions class="px-0">
          <v-btn @click="$router.go(-1)">{{
            $vuetify.lang.t('$vuetify.BACK')
          }}</v-btn>
          <v-btn
            v-if="user.isAdmin || user.isPlaceAdmin"
            color="success"
            @click="editGateControllerDialog = true"
          >
            <v-icon>mdi-plus</v-icon>
            {{ $vuetify.lang.t('$vuetify.GATE_CONTROLLERS.ADD_NEW') }}
          </v-btn>
        </v-card-actions>
      </v-col>
    </v-row>
    <v-toolbar v-else class="elevation-0 mt-12">
      <v-spacer></v-spacer>
      <v-progress-circular
        indeterminate
        color="secondary"
      ></v-progress-circular>
      <v-spacer></v-spacer>
    </v-toolbar>
    <edit-gate-controller-dialog
      v-if="selectedController || editGateControllerDialog"
      :controller="selectedController"
      :place-id="place.Id"
      :visible="editGateControllerDialog"
      @close="closeEditGateControllerDialog()"
    />
    <controller-logs-dialog
      v-if="selectedController && controllerLogsDialog"
      :controller-id="selectedController.ControllerId"
      :place-id="place.Id"
      :visible="controllerLogsDialog"
      @close="closeControllerLogsDialog()"
    />
    <controller-firmware-update-dialog
      v-if="selectedController && firmwareUpdateDialog"
      :controller="selectedController"
      :place-id="place.Id"
      :visible="firmwareUpdateDialog"
      @close="closeControllerFirmwareUpdateDialog()"
    />
  </v-container>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import formatDate from '@/mixins/formatDate'

export default {
  name: 'GateControllers',
  components: {
    EditGateControllerDialog: () =>
      import(
        /* webpackChunkName: "EditGateControllerDialog" */ '@/components/EditGateControllerDialog'
      ),
    ControllerLogsDialog: () =>
      import(
        /* webpackChunkName: "ControllerLogsDialog" */ '@/components/ControllerLogsDialog'
      ),
    ControllerFirmwareUpdateDialog: () =>
      import(
        /* webpackChunkName: "ControllerFirmwareUpdateDialog" */ '@/components/ControllerFirmwareUpdateDialog'
      )
  },
  mixins: [formatDate],
  data() {
    return {
      sendMode: 'NETWORK',
      editGateControllerDialog: false,
      controllersLoaded: false,
      controllerLogsDialog: false,
      selectedGate: undefined,
      gateIsAlwaysOpened: false,
      firmwareUpdateDialog: false
    }
  },
  computed: {
    ...mapGetters('app', ['loading', 'theme']),
    ...mapGetters('authentication', ['user']),
    ...mapGetters('places', [
      'place',
      'loadingControllers',
      'controllers',
      'refreshingControllers',
      'openingOrClosingGate',
      'selectedController'
    ])
  },
  created() {
    this.getPlace(this.$route.params.id)
    this.getGateControllers(this.$route.params.id)
  },
  methods: {
    ...mapActions('places', [
      'getGateControllers',
      'getPlace',
      'sendGateControllerCommand'
    ]),
    sendOpenCommandToController(controller, gateId) {
      this.sendGateControllerCommand({
        placeId: this.place.Id,
        controllerId: controller.Id,
        gateId,
        sendMode: 'NETWORK',
        command: 'OPEN',
        forGate: true
      })
    },
    sendCommandToController(controller, command, ssendMode) {
      this.$store.commit('places/setSelectedController', controller)
      switch (command) {
        case 'STATUS':
          this.sendGateControllerCommand({
            placeId: this.place.Id,
            controllerId: controller.Id,
            sendMode: 'NETWORK',
            command
          })
          break

        default:
          this.$confirm(this.$vuetify.lang.t('$vuetify.CAN_NOT_BE_UNDONE'), {
            title: this.$vuetify.lang.t('$vuetify.ARE_YOU_SURE'),
            color: 'error',
            icon: 'mdi-alert',
            persistent: true,
            buttonTrueText: this.$vuetify.lang.t('$vuetify.YES'),
            buttonFalseText: this.$vuetify.lang.t('$vuetify.CANCEL')
          }).then(option => {
            if (option === true) {
              this.sendGateControllerCommand({
                placeId: this.place.Id,
                controllerId: controller.Id,
                sendMode: ssendMode || 'NETWORK',
                command
              })
            }
          })
          break
      }
    },
    switchChange(controller, gateIndex) {
      this.selectedGate = controller.Gates[gateIndex]
      this.$store.commit('places/setSelectedController', controller)
      if (
        controller.Gates[gateIndex].isAlwaysOpened ||
        this.isInhibitInputHigh(controller, controller.Gates[gateIndex])
      ) {
        this.sendGateControllerCommand({
          placeId: this.place.Id,
          controllerId: controller.Id,
          outputList: [controller.Gates[gateIndex].GateNumber],
          gateId: controller.Gates[gateIndex].ID,
          sendMode: 'NETWORK',
          command: 'RELEASE_KEEP_OPEN',
          gateIndex
        })
        this.gateIsAlwaysOpened = false
      } else {
        this.sendGateControllerCommand({
          placeId: this.place.Id,
          controllerId: controller.Id,
          outputList: [controller.Gates[gateIndex].GateNumber],
          gateId: controller.Gates[gateIndex].ID,
          sendMode: 'NETWORK',
          command: 'KEEP_OPEN',
          gateIndex
        })
        this.gateIsAlwaysOpened = true
      }
      //   this.sendGateControllerCommand({
      //       placeId: this.place.Id,
      //       controllerId: controller.Id,
      //       outputList: [],
      //       gateId: controller.Gates[gateIndex].ID,
      //       sendMode: 'NETWORK',
      //       command: 'STATUS',
      //       gateIndex
      //     })
      setTimeout(() => {
        this.sendCommandToController(controller, 'STATUS')
      }, 1000)
    },
    secondsToHms(d) {
      d = Number(d)
      const h = Math.floor(d / 3600)
      const m = Math.floor((d % 3600) / 60)
      const s = Math.floor((d % 3600) % 60)

      const hDisplay = h > 0 ? h + (h === 1 ? ' hour, ' : ' hours, ') : ''
      const mDisplay = m > 0 ? m + (m === 1 ? ' minute, ' : ' minutes, ') : ''
      const sDisplay = s > 0 ? s + (s === 1 ? ' second' : ' seconds') : ''
      return hDisplay + mDisplay + sDisplay
    },
    returnValueFromZeroToHundred(rssi) {
      let percent
      if (rssi <= -85) {
        percent = 10
      }
      if (rssi >= -84 && rssi <= -71) {
        percent = 25
      }
      if (rssi >= -70 && rssi <= -60) {
        percent = 40
      }
      if (rssi >= -59 && rssi <= -50) {
        percent = 65
      }
      if (rssi >= -50) {
        percent = 98
      }
      return percent
    },
    openControllerLogsDialog(controller) {
      this.$store.commit('places/setSelectedController', controller)
      this.controllerLogsDialog = true
    },
    openEditControllerDialog(controller) {
      this.$store.commit('places/setSelectedController', controller)
      this.editGateControllerDialog = true
    },
    openFirmwareUpdateDialog(controller) {
      this.$store.commit('places/setSelectedController', controller)
      this.firmwareUpdateDialog = true
    },
    closeEditGateControllerDialog() {
      this.editGateControllerDialog = false
      this.$store.commit('places/setSelectedController', undefined)
      this.getGateControllers(this.$route.params.id)
    },
    closeControllerLogsDialog() {
      this.controllerLogsDialog = false
      this.$store.commit('places/setSelectedController', undefined)
    },
    closeControllerFirmwareUpdateDialog() {
      this.firmwareUpdateDialog = false
      this.$store.commit('places/setSelectedController', undefined)
    },
    isInhibitInputHigh(controller, gate) {
      let outputList = controller.OutputPinStateList
      if (outputList && gate.GateNumber) {
        outputList = outputList.split(',')
        return outputList[gate.GateNumber - 1].toString().split(':')[1] === '1'
      }
      return false
    }
  }
}
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
}

$signal-strength-bar-height: 15px;
$signal-strength-bar-width: 22px;

.sizing-box {
  height: $signal-strength-bar-height;
  width: $signal-strength-bar-width;
}

.signal-bars {
  display: inline-block;
}

.signal-bars .bar {
  width: 14%;
  min-height: 20%;
  display: inline-block;
}
.signal-bars .bar.first-bar {
  height: 20%;
}
.signal-bars .bar.second-bar {
  height: 40%;
}
.signal-bars .bar.third-bar {
  height: 60%;
}
.signal-bars .bar.fourth-bar {
  height: 80%;
}
.signal-bars .bar.fifth-bar {
  height: 99%;
}

.good .bar {
  background-color: #16a085;
  border: thin solid darken(#16a085, 7%);
}
.bad .bar {
  background-color: #e74c3c;
  border: thin solid darken(#e74c3c, 20%);
}
.ok .bar {
  background-color: #f1c40f;
  border: thin solid darken(#f1c40f, 7%);
}

.four-bars .bar.fifth-bar,
.three-bars .bar.fifth-bar,
.three-bars .bar.fourth-bar,
.one-bar .bar:not(.first-bar),
.two-bars .bar:not(.first-bar):not(.second-bar) {
  background-color: #fafafa;
  border: thin solid #f3f3f3;
}
</style>
